<template>
  <div class="container-fluid h-100 p-0">
    <div class="row h-100 g-0">
      <!-- Left Side Form -->
      <div class="col-12 col-md-3 col-lg-3 h-100">
        <div
          class="overflow-y-auto h-100 pt-4 px-3"
          style="height: calc(100vh - 60px)"
        >
          <!-- Kit Section -->
          <section class="mb-3 pb-3 border-bottom">
            <h3 class="mb-3">
              {{ $t("project.panelsPositioningComponent.kitConfiguration") }}
            </h3>
            <div class="mb-3">
              <label class="form-label">{{
                $t("project.panelsPositioningComponent.selectKit")
              }}</label>
              <select
                v-model="selectedKit"
                class="form-select"
                @change="handleKitSelection"
              >
                <option value="">
                  {{ $t("project.panelsPositioningComponent.customize") }}
                </option>
                <option
                  v-for="kit in availableKits"
                  :key="kit.id"
                  :value="kit.id"
                >
                  {{ kit.name }}
                </option>
              </select>
            </div>
          </section>
          <!-- Panels Section -->
          <section class="mb-3 pb-3 border-bottom">
            <h3 class="mb-3">
              {{ $t("project.panelsPositioningComponent.panelConfiguration") }}
            </h3>
            <div v-if="isLoading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{
                  $t("project.panelsPositioningComponent.loading")
                }}</span>
              </div>
            </div>
            <div v-else-if="error" class="alert alert-danger">
              {{ error }}
            </div>
            <div
              v-else
              v-for="(panel, index) in panels"
              :key="'panel-' + index"
              class="panel-config position-relative"
            >
              <button
                v-if="panels.length > 1"
                @click="removePanel(index)"
                class="btn btn-close position-absolute"
                style="right: 10px; top: 10px"
                aria-label="Delete panel"
              ></button>
              <div class="mb-3">
                <label class="form-label">{{
                  $t("project.panelsPositioningComponent.panelModel")
                }}</label>
                <select v-model="panel.device_id" class="form-select">
                  <option value="">
                    {{ $t("project.panelsPositioningComponent.selectModel") }}
                  </option>
                  <option
                    v-for="panelModel in equipmentStore.panels"
                    :key="panelModel.id"
                    :value="panelModel.id"
                  >
                    {{ panelModel.manufacturer }} {{ panelModel.model_number }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">{{
                  $t("project.panelsPositioningComponent.numberOfPanels")
                }}</label>
                <input
                  type="number"
                  v-model="panel.pcs"
                  class="form-control"
                  min="1"
                />
              </div>
              <button
                v-if="false"
                @click="addPanel"
                class="btn btn-outline-primary btn-sm"
              >
                {{ $t("project.panelsPositioningComponent.addAnotherPanel") }}
              </button>
            </div>
          </section>

          <!-- Inverter Section -->
          <section class="mb-3 pb-3">
            <h3 class="mb-3">
              {{
                $t("project.panelsPositioningComponent.inverterConfiguration")
              }}
            </h3>
            <div v-if="isLoading" class="text-center py-3">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{
                  $t("project.panelsPositioningComponent.loading")
                }}</span>
              </div>
            </div>
            <div v-else-if="error" class="alert alert-danger">
              {{ error }}
            </div>
            <div
              v-else
              v-for="(inverter, index) in inverters"
              :key="'inverter-' + index"
              class="inverter-config position-relative"
            >
              <button
                v-if="inverters.length > 1"
                @click="removeInverter(index)"
                class="btn btn-close position-absolute"
                style="right: 10px; top: 10px"
                aria-label="Delete inverter"
              ></button>
              <div class="mb-3">
                <label class="form-label">{{
                  $t("project.panelsPositioningComponent.inverterModel")
                }}</label>
                <select v-model="inverter.device_id" class="form-select">
                  <option value="">
                    {{ $t("project.panelsPositioningComponent.selectModel") }}
                  </option>
                  <option
                    v-for="inv in equipmentStore.inverters"
                    :key="inv.id"
                    :value="inv.id"
                  >
                    {{ inv.manufacturer }} {{ inv.model_number }}
                  </option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">{{
                  $t("project.panelsPositioningComponent.numberOfUnits")
                }}</label>
                <input
                  type="number"
                  v-model="inverter.pcs"
                  class="form-control"
                  min="1"
                />
              </div>
              <button
                v-if="index === inverters.length - 1"
                @click="addInverter"
                class="btn btn-outline-primary btn-sm"
              >
                {{ $t("project.panelsPositioningComponent.addAnotherInverter") }}
                <i class="bi bi-plus ms-2"></i>
              </button>
            </div>
          </section>
        </div>
      </div>

      <!-- Right Side Empty Area -->
      <div class="col-12 col-md-9 col-lg-9 h-100 d-none d-md-block p-4">
        <div
          class="position-fixed h-100 drawing-component-container"
          style="top: 90px; left: 33%; right: 10px"
        >
          <DrawingComponent
            class="h-85"
            :selected-panel-model="selectedPanelModel"
            :total-panels="totalPanels"
            @update:drawingData="handleDrawingDataUpdate"
          />
        </div>
      </div>

      <!-- Mobile View -->
      <div class="col-12 d-md-none p-4" style="height: 250px">
        <DrawingComponent
          :selected-panel-model="selectedPanelModel"
          @update:drawingData="handleDrawingDataUpdate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  computed,
  watch,
} from "vue";
import { useEquipmentStore } from "@/stores/equipment";
import { useMapStateStore } from "@/stores/mapState";
import { useProjectStore } from "@/stores/project";
import { storeToRefs } from "pinia";
import DrawingComponent from "@/components/DrawingComponent.vue";
import type { ProjectDevice, Kit } from "@/types/projectTypes";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "ProjectPanelsPositioningComponent",
  components: {
    DrawingComponent,
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const equipmentStore = useEquipmentStore();
    const mapStateStore = useMapStateStore();
    const projectStore = useProjectStore();
    const { project } = storeToRefs(projectStore);

    // Use ProjectDevice instead of separate interfaces
    const inverters = ref<Partial<ProjectDevice>[]>([
      { device_type: "Inverter", pcs: 1 },
    ]);
    const panels = ref<Partial<ProjectDevice>[]>([
      { device_type: "Panel", pcs: 1 },
    ]);

    // Get kits from equipment store
    const availableKits = computed(() => equipmentStore.kits);
    const selectedKit = ref<string>("");

    // Initialize from existing project data if available
    onMounted(() => {
      const loadDevices = () => {
        if (project.value?.devices) {
          // Load existing panels
          const existingPanels = project.value.devices
            .filter((device) => device.device_type === "Panel")
            .map((device) => ({
              device_type: "Panel",
              device_id: device.device_id,
              pcs: device.pcs,
            }));
          panels.value =
            existingPanels.length > 0
              ? existingPanels
              : [{ device_type: "Panel", pcs: 1 }];

          // Load existing inverters
          const existingInverters = project.value.devices
            .filter((device) => device.device_type === "Inverter")
            .map((device) => ({
              device_type: "Inverter",
              device_id: device.device_id,
              pcs: device.pcs,
            }));
          inverters.value =
            existingInverters.length > 0
              ? existingInverters
              : [{ device_type: "Inverter", pcs: 1 }];
        }

        // Load selected kit if it exists
        if (project.value?.selected_kit_id) {
          selectedKit.value = project.value.selected_kit_id.toString();
          console.log("[onMounted] Loaded selected kit ID:", selectedKit.value);
        }
      };

      // Ensure equipment data is loaded first
      if (
        equipmentStore.panels.length === 0 ||
        equipmentStore.inverters.length === 0 ||
        equipmentStore.kits.length === 0
      ) {
        console.log("Equipment data not found, fetching...");
        equipmentStore.fetchEquipment().then(() => {
          console.log("Equipment data loaded:", {
            panels: equipmentStore.panels,
            inverters: equipmentStore.inverters,
            kits: equipmentStore.kits,
          });
          loadDevices();
        });
      } else {
        console.log("Existing equipment data:", {
          panels: equipmentStore.panels,
          inverters: equipmentStore.inverters,
          kits: equipmentStore.kits,
        });
        loadDevices();
      }
    });

    // Helper function to create a ProjectDevice
    const createDevice = (
      type: "Panel" | "Inverter",
      deviceId: number,
      count: number
    ): ProjectDevice | null => {
      if (!project.value) return null;

      return {
        project: project.value.id,
        device_type: type,
        device_id: deviceId,
        pcs: count,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
    };

    // Helper function to update devices in project store
    const updateDevices = () => {
      if (!project.value) return;

      // Get current devices and filter out panels and inverters
      const currentDevices = project.value.devices || [];
      const otherDevices = currentDevices.filter(
        (device) =>
          device.device_type.toLowerCase() !== "panel" &&
          device.device_type.toLowerCase() !== "inverter"
      );

      // Create a map of existing devices by their unique key (device_type + device_id)
      const existingDevicesMap = new Map(
        currentDevices.map((device) => [
          `${device.device_type.toLowerCase()}-${device.device_id}`,
          device,
        ])
      );

      // Process panels
      const updatedPanelDevices = panels.value
        .filter((p) => p.device_id && p.pcs && p.pcs > 0)
        .map((p) => {
          const uniqueKey = `panel-${p.device_id}`;
          const existingDevice = existingDevicesMap.get(uniqueKey);

          if (existingDevice) {
            // Update existing device
            return {
              ...existingDevice,
              pcs: p.pcs,
              updated_at: new Date().toISOString(),
            };
          } else {
            // Create new device
            return createDevice("Panel", p.device_id!, p.pcs!);
          }
        })
        .filter((device): device is ProjectDevice => device !== null);

      // Process inverters
      const updatedInverterDevices = inverters.value
        .filter((i) => i.device_id && i.pcs && i.pcs > 0)
        .map((i) => {
          const uniqueKey = `inverter-${i.device_id}`;
          const existingDevice = existingDevicesMap.get(uniqueKey);

          if (existingDevice) {
            // Update existing device
            return {
              ...existingDevice,
              pcs: i.pcs,
              updated_at: new Date().toISOString(),
            };
          } else {
            // Create new device
            return createDevice("Inverter", i.device_id!, i.pcs!);
          }
        })
        .filter((device): device is ProjectDevice => device !== null);

      // Combine all devices: other devices + updated panels + updated inverters
      const allDevices = [
        ...otherDevices,
        ...updatedPanelDevices,
        ...updatedInverterDevices,
      ];
      projectStore.setProjectDevices(allDevices);

      // Derive project phase_type from selected inverters
      try {
        const selectedInverterIds = inverters.value
          .filter((i) => i.device_id && i.pcs && i.pcs > 0)
          .map((i) => i.device_id!) as number[];
        if (selectedInverterIds.length > 0) {
          const selectedInverterModels = equipmentStore.inverters.filter((inv) =>
            selectedInverterIds.includes(inv.id)
          );
          const hasThreePhase = selectedInverterModels.some(
            (inv) => inv.phase_type === "three"
          );
          const derivedPhase = hasThreePhase ? "three" : "single";
          if (project.value.phase_type !== derivedPhase) {
            projectStore.setPhaseType(derivedPhase);
          }
        }
      } catch (e) {
        console.warn("[updateDevices] Failed to derive phase_type from inverters", e);
      }
    };

    // Watch for changes in panels and inverters
    watch(
      [panels, inverters],
      () => {
        updateDevices();
      },
      { deep: true }
    );

    // Computed properties for loading and error states
    const isLoading = computed(() => equipmentStore.isLoading);
    const error = computed(() => equipmentStore.error);

    // Computed property for selected panel model
    const selectedPanelModel = computed(() => {
      // Get the first panel that has a model selected
      const selectedPanel = panels.value.find((p) => p.device_id);
      if (!selectedPanel) return null;

      const panelModel = equipmentStore.panels.find(
        (p) => p.id === selectedPanel.device_id
      );
      return panelModel ? panelModel.model_number : null;
    });

    // Computed property for total number of panels
    const totalPanels = computed(() => {
      return panels.value.reduce((total, panel) => {
        return total + (panel.device_id ? panel.pcs! : 0);
      }, 0);
    });

    const addInverter = () => {
      inverters.value.push({ device_type: "inverter", pcs: 1 });
    };

    const addPanel = () => {
      panels.value.push({ device_type: "panel", pcs: 1 });
    };

    const removeInverter = (index: number) => {
      inverters.value.splice(index, 1);
    };

    const removePanel = (index: number) => {
      panels.value.splice(index, 1);
    };

    // Add new method to handle drawing data updates
    const handleDrawingDataUpdate = (drawingData: any) => {
      if (project.value) {
        projectStore.setDrawingData(drawingData);
      }
    };

    // Function to handle kit selection
    const handleKitSelection = () => {
      // Save kit selection to project store
      if (selectedKit.value) {
        projectStore.setSelectedKit(selectedKit.value);
      } else {
        // Clear kit selection if "Customize" is selected
        projectStore.setSelectedKit("");
      }

      if (!selectedKit.value) {
        // Reset to default state if "Customize" is selected
        panels.value = [{ device_type: "Panel", pcs: 1 }];
        inverters.value = [{ device_type: "Inverter", pcs: 1 }];
        return;
      }

      const selectedKitData = equipmentStore.getKitById(selectedKit.value);
      if (!selectedKitData) {
        console.error(`Kit with ID ${selectedKit.value} not found`);
        return;
      }

      console.log(
        "Selected kit data:",
        JSON.stringify(selectedKitData, null, 2)
      );

      // Update panels if they exist in the kit
      if (
        selectedKitData.panels &&
        Array.isArray(selectedKitData.panels) &&
        selectedKitData.panels.length > 0
      ) {
        // Process panels using kit data for id/quantity, equipment store for validation
        // Type assertion to handle the mismatch between Kit interface and actual API response
        const flatPanels = selectedKitData.panels as unknown as Array<{ id: number; quantity: number }>;
        const panelDevices = flatPanels
          .filter((panelKit: { id: number; quantity: number }) => {
            // Verify the panel exists in equipment store
            const panelExists = equipmentStore.panels.find(p => p.id === panelKit.id);
            if (!panelExists) {
              console.warn(`Panel with ID ${panelKit.id} not found in equipment store`);
              return false;
            }
            return panelKit.quantity && panelKit.quantity > 0;
          })
          .map((panelKit: { id: number; quantity: number }) => ({
            device_type: "Panel",
            device_id: panelKit.id,
            pcs: panelKit.quantity,
          }));

        if (panelDevices.length > 0) {
          panels.value = panelDevices;
        } else {
          console.warn(`Kit ${selectedKit.value} has no valid panels data`);
          panels.value = [{ device_type: "Panel", pcs: 1 }];
        }
      } else {
        console.warn(`Kit ${selectedKit.value} has no panels data`);
        panels.value = [{ device_type: "Panel", pcs: 1 }];
      }

      // Update inverters if they exist in the kit
      if (
        selectedKitData.inverters &&
        Array.isArray(selectedKitData.inverters) &&
        selectedKitData.inverters.length > 0
      ) {
        // Process inverters using kit data for id/quantity, equipment store for validation
        // Type assertion to handle the mismatch between Kit interface and actual API response
        const flatInverters = selectedKitData.inverters as unknown as Array<{ id: number; quantity: number }>;
        const inverterDevices = flatInverters
          .filter((inverterKit: { id: number; quantity: number }) => {
            // Verify the inverter exists in equipment store
            const inverterExists = equipmentStore.inverters.find(i => i.id === inverterKit.id);
            if (!inverterExists) {
              console.warn(`Inverter with ID ${inverterKit.id} not found in equipment store`);
              return false;
            }
            return inverterKit.quantity && inverterKit.quantity > 0;
          })
          .map((inverterKit: { id: number; quantity: number }) => ({
            device_type: "Inverter",
            device_id: inverterKit.id,
            pcs: inverterKit.quantity,
          }));

        if (inverterDevices.length > 0) {
          inverters.value = inverterDevices;
        } else {
          console.warn(`Kit ${selectedKit.value} has no valid inverters data`);
          inverters.value = [{ device_type: "Inverter", pcs: 1 }];
        }
      } else {
        console.warn(`Kit ${selectedKit.value} has no inverters data`);
        inverters.value = [{ device_type: "Inverter", pcs: 1 }];
      }

      // Process batteries if they exist in the kit
      let kitBatteries: Partial<ProjectDevice>[] = [];
      if (
        selectedKitData.batteries &&
        Array.isArray(selectedKitData.batteries) &&
        selectedKitData.batteries.length > 0
      ) {
        // Type assertion to handle the mismatch between Kit interface and actual API response
        const flatBatteries = selectedKitData.batteries as unknown as Array<{ id: number; quantity: number }>;
        kitBatteries = flatBatteries
          .filter((batteryKit: { id: number; quantity: number }) => {
            // Verify the battery exists in equipment store
            const batteryExists = equipmentStore.batteries.find(b => b.id === batteryKit.id);
            if (!batteryExists) {
              console.warn(`Battery with ID ${batteryKit.id} not found in equipment store`);
              return false;
            }
            return batteryKit.quantity && batteryKit.quantity > 0;
          })
          .map((batteryKit: { id: number; quantity: number }) => ({
            device_type: "Battery",
            device_id: batteryKit.id,
            pcs: batteryKit.quantity,
          }));
      }

      // Process EV chargers if they exist in the kit
      let kitEVChargers: Partial<ProjectDevice>[] = [];
      if (
        selectedKitData.ev_chargers &&
        Array.isArray(selectedKitData.ev_chargers) &&
        selectedKitData.ev_chargers.length > 0
      ) {
        // Type assertion to handle the mismatch between Kit interface and actual API response
        const flatEVChargers = selectedKitData.ev_chargers as unknown as Array<{ id: number; quantity: number }>;
        kitEVChargers = flatEVChargers
          .filter((chargerKit: { id: number; quantity: number }) => {
            // Verify the EV charger exists in equipment store
            const chargerExists = equipmentStore.evChargers.find(e => e.id === chargerKit.id);
            if (!chargerExists) {
              console.warn(`EV Charger with ID ${chargerKit.id} not found in equipment store`);
              return false;
            }
            return chargerKit.quantity && chargerKit.quantity > 0;
          })
          .map((chargerKit: { id: number; quantity: number }) => ({
            device_type: "EVCharger",
            device_id: chargerKit.id,
            pcs: chargerKit.quantity,
          }));
      }

      // Process meters if they exist in the kit
      let kitMeters: Partial<ProjectDevice>[] = [];
      if (
        selectedKitData.meters &&
        Array.isArray(selectedKitData.meters) &&
        selectedKitData.meters.length > 0
      ) {
        // Type assertion to handle the mismatch between Kit interface and actual API response
        const flatMeters = selectedKitData.meters as unknown as Array<{ id: number; quantity: number }>;
        kitMeters = flatMeters
          .filter((meterKit: { id: number; quantity: number }) => {
            // Verify the meter exists in equipment store
            const meterExists = equipmentStore.meters.find(m => m.id === meterKit.id);
            if (!meterExists) {
              console.warn(`Meter with ID ${meterKit.id} not found in equipment store`);
              return false;
            }
            return meterKit.quantity && meterKit.quantity > 0;
          })
          .map((meterKit: { id: number; quantity: number }) => ({
            device_type: "Meter",
            device_id: meterKit.id,
            pcs: meterKit.quantity,
          }));
      }

      // Update all devices in the project store
      if (project.value) {
        // Get current devices and filter out panels, inverters, batteries, EV chargers, and meters
        const currentDevices = project.value.devices || [];
        const otherDevices = currentDevices.filter(
          (device) =>
            device.device_type !== "Panel" &&
            device.device_type !== "Inverter" &&
            device.device_type !== "Battery" &&
            device.device_type !== "EVCharger" &&
            device.device_type !== "Meter"
        );

        // Create ProjectDevice objects from our partial devices
        const createFullDevice = (
          device: Partial<ProjectDevice>
        ): ProjectDevice | null => {
          if (!project.value || !device.device_id || !device.pcs) return null;
          return {
            project: project.value.id,
            device_type: device.device_type!,
            device_id: device.device_id,
            pcs: device.pcs,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
        };

        // Filter out invalid devices and convert to full ProjectDevice objects
        const panelDevices = panels.value
          .filter((p) => p.device_id && p.pcs && p.pcs > 0)
          .map((p) => createFullDevice(p))
          .filter((device): device is ProjectDevice => device !== null);

        const inverterDevices = inverters.value
          .filter((i) => i.device_id && i.pcs && i.pcs > 0)
          .map((i) => createFullDevice(i))
          .filter((device): device is ProjectDevice => device !== null);

        const batteryDevices = kitBatteries
          .filter((b) => b.device_id && b.pcs && b.pcs > 0)
          .map((b) => createFullDevice(b))
          .filter((device): device is ProjectDevice => device !== null);

        const evChargerDevices = kitEVChargers
          .filter((e) => e.device_id && e.pcs && e.pcs > 0)
          .map((e) => createFullDevice(e))
          .filter((device): device is ProjectDevice => device !== null);

        const meterDevices = kitMeters
          .filter((m) => m.device_id && m.pcs && m.pcs > 0)
          .map((m) => createFullDevice(m))
          .filter((device): device is ProjectDevice => device !== null);

        // Combine all devices
        const allDevices = [
          ...otherDevices,
          ...panelDevices,
          ...inverterDevices,
          ...batteryDevices,
          ...evChargerDevices,
          ...meterDevices,
        ];

        // Update the project store with all devices
        projectStore.setProjectDevices(allDevices);
      }
    };

    return {
      inverters,
      panels,
      addInverter,
      addPanel,
      removeInverter,
      removePanel,
      isLoading,
      error,
      equipmentStore,
      mapStateStore,
      selectedPanelModel,
      totalPanels,
      handleDrawingDataUpdate,
      selectedKit,
      availableKits,
      handleKitSelection,
    };
  },
});
</script>

<style scoped>
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background-color: var(--color-light-gray);
  border-radius: 2px;
}

@media (max-width: 768px) {
  .container-fluid {
    padding-left: 60px; /* Account for fixed sidebar on mobile */
  }
}

.form-control {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-control:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.inverter-config,
.panel-config {
  padding: 15px;
  margin-bottom: 15px;
  background-color: var(--color-light-light-gray);
  border-radius: 2px;
}

.form-label {
  font-weight: 500;
  color: var(--color-dark);
}

.btn-outline-primary {
  width: 70%;
  margin-top: 10px;
  margin-left: auto;
  display: block;
  border-radius: 2px;
  color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  background-color: transparent !important;
}

.btn-outline-primary:hover {
  color: var(--color-white) !important;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
}

.btn-primary {
  width: 100%;
  margin-top: 20px;
  border-radius: 2px;
}

.h-85 {
  height: 85%;
}

.form-select {
  border-radius: 2px;
}

.btn-close {
  border-radius: 2px;
}
</style>
